import React, { useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import JigsawPlayer from './JigsawPlayer';
import { JIGSAW_PUZZLES, DIFFICULTY_CONFIG, PUZZLE_CATEGORIES } from '../../data/puzzles';
import { theme } from '../../styles/theme';
import { completePuzzle } from '../../services/userProfileService';

const JigsawGame: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const puzzle = JIGSAW_PUZZLES.find(p => p.id === id);
    const [difficulty, setDifficulty] = useState<string | null>(null);

    if (!puzzle) {
        return (
            <div className="container" style={{ padding: '40px 16px', textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>😕</div>
                <h2 style={{ color: theme.colors.textPrimary }}>Puzzle not found</h2>
                <Link to="/" style={{
                    marginTop: '18px',
                    padding: '10px 18px',
                    background: theme.gradients.primary,
                    color: theme.colors.textLight,
                    textDecoration: 'none',
                    borderRadius: '10px',
                    display: 'inline-block'
                }}>Back to Gallery</Link>
            </div>
        );
    }

    // Difficulty selection screen
    if (!difficulty) {
        const category = PUZZLE_CATEGORIES.find(c => c.id === puzzle.category);
        return (
            <div style={{ minHeight: '100vh', background: theme.gradients.nature, padding: '28px 14px' }}>
                <button
                    onClick={() => history.push('/')}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        padding: '10px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    ← Back
                </button>

                <div style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'center', color: theme.colors.textPrimary }}>
                    <h1 style={{ marginBottom: '10px' }}>{puzzle.title}</h1>
                    <div style={{
                        display: 'inline-block',
                        padding: '6px 16px',
                        background: category?.color || theme.colors.primary,
                        borderRadius: '20px',
                        fontSize: '14px',
                        marginBottom: '30px'
                    }}>
                        {category?.name}
                    </div>

                    {/* Preview image */}
                    <div style={{ width: '100%', maxWidth: '520px', margin: '0 auto 26px', borderRadius: '12px', overflow: 'hidden', boxShadow: theme.shadows.lg }}>
                        <img
                            src={puzzle.url}
                            alt={puzzle.title}
                            style={{ width: '100%', display: 'block' }}
                            crossOrigin="anonymous"
                        />
                    </div>

                    <h2 style={{ marginBottom: '18px', color: theme.colors.textPrimary }}>Select Difficulty</h2>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {Object.entries(DIFFICULTY_CONFIG).map(([key, config]) => (
                            <button
                                key={key}
                                onClick={() => setDifficulty(key)}
                                style={{
                                    padding: '14px 22px',
                                    fontSize: '16px',
                                    background: config.color,
                                    color: theme.colors.textLight,
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    transition: 'transform 0.18s, box-shadow 0.18s',
                                    boxShadow: theme.shadows.sm
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                <div style={{ fontWeight: 'bold' }}>{config.label}</div>
                                <div style={{ fontSize: '14px', opacity: 0.8 }}>
                                    {config.rows * config.cols} pieces
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const config = DIFFICULTY_CONFIG[difficulty];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', background: theme.colors.bgPrimary, minHeight: '100vh' }}>
            {/* Header bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 16px',
                background: theme.colors.cardBg,
                borderBottom: `1px solid ${theme.colors.cardBorder}`,
                flexWrap: 'wrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <button
                    onClick={() => setDifficulty(null)}
                    style={{
                        background: theme.colors.bgPrimary,
                        padding: '8px 14px',
                        borderRadius: '8px',
                        border: `1px solid ${theme.colors.cardBorder}`,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '13px',
                        color: theme.colors.textPrimary,
                        boxShadow: theme.shadows.sm,
                    }}
                >
                    ← Change
                </button>
                <span style={{ color: theme.colors.textPrimary, fontSize: '16px', fontWeight: 700, flex: 1, minWidth: '100px' }}>
                    {puzzle.title}
                </span>
                <span style={{
                    background: config.color,
                    padding: '6px 12px',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 600
                }}>
                    {config.label} ({config.rows}×{config.cols})
                </span>
            </div>

            {/* Puzzle area with proper spacing */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px 16px', overflow: 'auto' }}>
                <JigsawPlayer
                    imageUrl={puzzle.url}
                    rows={config.rows}
                    cols={config.cols}
                    onComplete={() => {
                        completePuzzle(puzzle.id, puzzle.category, difficulty as any, puzzle.xpReward, puzzle.coinReward);
                    }}
                />
            </div>
        </div>
    );
};

export default JigsawGame;
